import { useState } from 'react';
import { useDispatch } from 'react-redux';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '@jumbo/constants/ActionTypes';
import { DEFAULT_DRIVER_DATA, DEFAULT_VEHICAL_CHECK_LIST } from '../Data/DefaultHFTaskData';
import { toLocalISO } from '../components/SharedFormFields';
import { useAllUsers } from 'context/AllUserDataContext';

// Task types that use GeneralTaskForm
const GENERAL_TASK_TYPES = [
  'Administration',
  'Moisture Content',
  'Clock Out',
  'Chainsaw',
  'Other',
  'Break',
  'Nominate Contact',
  'Cancel',
];

const REPAIR_TASK_TYPES = ['Repairs & Maintainance', 'External Repairs'];

const getDefaultFormData = task => ({
  task,
  employeeName: '',
  dateAndTime: toLocalISO(),
  forestSPNumber: '',
  additionalInformation: '',
});

const useGenTasksAddHook = (open, setOpen, handleSuccessfulSubmit) => {
  const dispatch = useDispatch();
  const { allUsers } = useAllUsers();

  const [selectedTask, setSelectedTask] = useState('');

  // Driving / Vehicle Check List
  const [drivingData, setDrivingData] = useState({
    ...DEFAULT_DRIVER_DATA,
    dateAndTime: toLocalISO(),
  });
  const [vehicalCheckList, setVehicalChecklist] = useState(DEFAULT_VEHICAL_CHECK_LIST);

  // General form
  const [generalFormData, setGeneralFormData] = useState({});

  // Expense claim
  const [expenseClaimData, setExpenseClaimData] = useState({});

  // Repair & Maintenance
  const [repairAndmaintananceData, setRepairAndmaintananceData] = useState({});

  // File uploads — mirrors edit hook pattern:
  // files = display state (shown as chips), also what gets uploaded
  const [files, setFiles] = useState([]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleTaskChange = task => {
    setSelectedTask(task);
    const defaults = getDefaultFormData(task);

    if (task === 'Vehicle Check List') {
      setDrivingData({ ...DEFAULT_DRIVER_DATA, ...defaults });
      setVehicalChecklist(DEFAULT_VEHICAL_CHECK_LIST);
    } else if (GENERAL_TASK_TYPES.includes(task)) {
      setGeneralFormData({
        ...defaults,
        workingMoreThan10kmAway: 'More than 10 hrs',
      });
    } else if (task === 'Expense Claim') {
      setExpenseClaimData(defaults);
    } else if (REPAIR_TASK_TYPES.includes(task)) {
      setRepairAndmaintananceData(defaults);
      setVehicalChecklist(DEFAULT_VEHICAL_CHECK_LIST);
    }
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setSelectedTask('');
    setDrivingData({ ...DEFAULT_DRIVER_DATA, dateAndTime: toLocalISO().substring(0, 16) });
    setVehicalChecklist(DEFAULT_VEHICAL_CHECK_LIST);
    setGeneralFormData({});
    setExpenseClaimData({});
    setRepairAndmaintananceData({});
    setFiles([]);
  };

  const EditVehicalCheckListData = (name, value) => {
    setVehicalChecklist(prev => ({ ...prev, [name]: value }));
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const submitNewTask = async () => {
    try {
      let payload = {
        submissionDate: toLocalISO(),
      };

      if (REPAIR_TASK_TYPES.includes(selectedTask)) {
        payload = { ...repairAndmaintananceData, task: selectedTask };
      } else if (selectedTask === 'Vehicle Check List') {
        payload = { ...drivingData, task: selectedTask };
      } else if (selectedTask === 'Expense Claim') {
        payload = { ...expenseClaimData, task: selectedTask };
      } else {
        payload = { ...generalFormData, task: selectedTask };
      }

      const username = allUsers?.find(u => u.defaultDriverDisplayName === payload.employeeName)?.email || 'Unknown User';
      payload = { ...payload, driverUserName: username };

      dispatch({ type: FETCH_START });

      const response = await customAxios.post('/GenTasks/AddTasks', payload, {
        headers: authHeader(),
      });

      if (response.status === 200 || response.status === 201) {
        // API returns the new task ID directly as a number
        const newTaskId = response.data;

        // Save vehicle checklist if applicable
        if (selectedTask === 'Vehicle Check List' || REPAIR_TASK_TYPES.includes(selectedTask)) {
          await saveVehicalCheckList({ ...vehicalCheckList, taskId: newTaskId });
        }

        // Upload files if any
        if (files.length > 0 && newTaskId) {
          await uploadDocs(newTaskId, selectedTask, files);
        }

        dispatch({ type: FETCH_SUCCESS });
        handleSuccessfulSubmit();
        resetForm();
      }
    } catch (err) {
      console.error('Error adding task:', err);
      dispatch({ type: FETCH_ERROR, payload: 'Error Adding Task' });
    }
  };

  const saveVehicalCheckList = async checklistPayload => {
    try {
      await customAxios.post('/GenTasks/AddTasksVehicleChecklist', checklistPayload, {
        headers: authHeader(),
      });
    } catch (err) {
      console.error('Error saving checklist:', err);
    }
  };

  const uploadDocs = async (taskId, taskType, filesToUpload) => {
    try {
      const formData = new FormData();
      formData.append('taskId', taskId);
      formData.append('docType', taskType);
      filesToUpload.forEach((file, i) => {
        // files may be raw File objects or normalised { _rawFile, documentName }
        const rawFile = file._rawFile || file;
        formData.append(`file[${i}]`, rawFile);
      });

      await customAxios.post('/GenTasks/AddTasksDocs', formData, { headers: { ...authHeader() } });
    } catch (err) {
      console.error('Error uploading docs:', err);
    }
  };

  return {
    selectedTask,
    handleTaskChange,
    handleClose,
    submitNewTask,
    // driving
    drivingData,
    setDrivingData,
    // checklist
    vehicalCheckList,
    EditVehicalCheckListData,
    // general
    generalFormData,
    setGeneralFormData,
    // expense
    expenseClaimData,
    setExpenseClaimData,
    // repair
    repairAndmaintananceData,
    setRepairAndmaintananceData,
    // files
    files,
    setFiles,
  };
};

export default useGenTasksAddHook;
